import { useState, useRef, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react'
import type { ReactNode } from 'react'
import { useMantineColorScheme } from '@mantine/core'

interface SlideUpDrawerProps {
    children: ReactNode
    onDragStateChange?: (isDragging: boolean) => void
    onModeChange?: (mode: 'mini' | 'normal' | 'expanded') => void
}

export interface SlideUpDrawerRef {
    expandToNormal: () => void
    expandToFull: () => void
    collapseToMini: () => void
}

export const SlideUpDrawer = forwardRef<SlideUpDrawerRef, SlideUpDrawerProps>(
    ({ children, onDragStateChange, onModeChange }, ref) => {
        const [drawerHeight, setDrawerHeight] = useState(80) // Start in mini mode
        const [isDragging, setIsDragging] = useState(false)
        const [startY, setStartY] = useState(0)
        const [startHeight, setStartHeight] = useState(0)
        const [isExpanded, setIsExpanded] = useState(false)
        const [isMini, setIsMini] = useState(true) // Start in mini state

        const drawerRef = useRef<HTMLDivElement>(null)
        const contentRef = useRef<HTMLDivElement>(null)
        const { colorScheme } = useMantineColorScheme()

        // Resolve actual color scheme (handle 'auto' mode)
        const resolvedColorScheme = colorScheme === 'auto'
            ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
            : colorScheme

        const miniHeight = 80 // New mini height - just handle and text
        const minHeight = 350 // Normal collapsed height
        // Use a more conservative max height for iOS Safari
        const maxHeight = Math.min(
            (window.visualViewport?.height || window.innerHeight) * 0.8,
            600
        ) // Use visual viewport when available (iOS Safari)

        // Update drag state in parent component
        useEffect(() => {
            onDragStateChange?.(isDragging)
        }, [isDragging, onDragStateChange])

        // Update mode in parent component
        useEffect(() => {
            let mode: 'mini' | 'normal' | 'expanded'
            if (isMini) {
                mode = 'mini'
            } else if (isExpanded) {
                mode = 'expanded'
            } else {
                mode = 'normal'
            }
            onModeChange?.(mode)
        }, [isMini, isExpanded, onModeChange])

        // Handle touch start on handle area
        const handleTouchStart = useCallback((e: TouchEvent) => {
            if (!drawerRef.current) return

            const touch = e.touches[0]
            const rect = drawerRef.current.getBoundingClientRect()

            // Only start dragging if touch is on the handle area (top 60px of drawer)
            if (touch.clientY > rect.top + 60) return

            setIsDragging(true)
            setStartY(touch.clientY)
            setStartHeight(drawerHeight)

            // Prevent scrolling while dragging
            e.preventDefault()
        }, [drawerHeight])

        // Handle touch move
        const handleTouchMove = useCallback((e: TouchEvent) => {
            if (!isDragging) return

            const touch = e.touches[0]
            const deltaY = startY - touch.clientY // Positive when dragging up
            const newHeight = Math.min(Math.max(startHeight + deltaY, miniHeight), maxHeight)

            setDrawerHeight(newHeight)

            // Prevent scrolling while dragging
            e.preventDefault()
        }, [isDragging, startY, startHeight, miniHeight, maxHeight])

        // Handle touch end
        const handleTouchEnd = useCallback(() => {
            if (!isDragging) return

            setIsDragging(false)

            // Three-state snapping: mini, normal (collapsed), expanded
            const miniThreshold = miniHeight + (minHeight - miniHeight) * 0.5 // 50% between mini and normal
            const expandThreshold = minHeight + (maxHeight - minHeight) * 0.3 // 30% between normal and expanded

            if (drawerHeight < miniThreshold) {
                // Snap to mini state
                setDrawerHeight(miniHeight)
                setIsExpanded(false)
                setIsMini(true)
            } else if (drawerHeight < expandThreshold) {
                // Snap to normal collapsed state
                setDrawerHeight(minHeight)
                setIsExpanded(false)
                setIsMini(false)
            } else {
                // Snap to expanded state
                setDrawerHeight(maxHeight)
                setIsExpanded(true)
                setIsMini(false)
            }
        }, [isDragging, drawerHeight, miniHeight, minHeight, maxHeight])

        // Enhanced scroll handling for pull-to-expand
        const handleContentTouchStart = useCallback((e: TouchEvent) => {
            if (isDragging || isExpanded) return

            const content = contentRef.current
            if (!content) return

            // If we're at the top of the content and not expanded, prepare for potential expansion
            if (content.scrollTop <= 5) { // Small tolerance for scroll position
                const touch = e.touches[0]
                setStartY(touch.clientY)
            }
        }, [isDragging, isExpanded])

        const handleContentTouchMove = useCallback((e: TouchEvent) => {
            if (isDragging || isExpanded) return

            const content = contentRef.current
            if (!content) return

            // If we're at the top and user is pulling down (trying to scroll up beyond content)
            if (content.scrollTop <= 5) {
                const touch = e.touches[0]
                const deltaY = startY - touch.clientY

                // If pulling up significantly, expand the drawer
                if (deltaY > 50) {
                    setDrawerHeight(maxHeight)
                    setIsExpanded(true)
                    e.preventDefault()
                }
            }
        }, [isDragging, isExpanded, startY, maxHeight])

        // Set up touch event listeners for drawer handle
        useEffect(() => {
            const drawer = drawerRef.current
            if (!drawer) return

            drawer.addEventListener('touchstart', handleTouchStart, { passive: false })
            drawer.addEventListener('touchmove', handleTouchMove, { passive: false })
            drawer.addEventListener('touchend', handleTouchEnd)

            return () => {
                drawer.removeEventListener('touchstart', handleTouchStart)
                drawer.removeEventListener('touchmove', handleTouchMove)
                drawer.removeEventListener('touchend', handleTouchEnd)
            }
        }, [handleTouchStart, handleTouchMove, handleTouchEnd])

        // Set up touch event listeners for content area
        useEffect(() => {
            const content = contentRef.current
            if (!content) return

            content.addEventListener('touchstart', handleContentTouchStart, { passive: true })
            content.addEventListener('touchmove', handleContentTouchMove, { passive: false })

            return () => {
                content.removeEventListener('touchstart', handleContentTouchStart)
                content.removeEventListener('touchmove', handleContentTouchMove)
            }
        }, [handleContentTouchStart, handleContentTouchMove])

        // Handle window resize and iOS Safari viewport changes
        useEffect(() => {
            const handleResize = () => {
                const newMaxHeight = Math.min(
                    (window.visualViewport?.height || window.innerHeight) * 0.8,
                    600
                )
                if (isExpanded) {
                    setDrawerHeight(newMaxHeight)
                }
            }

            // Listen to both window resize and visual viewport changes (iOS Safari)
            window.addEventListener('resize', handleResize)
            if (window.visualViewport) {
                window.visualViewport.addEventListener('resize', handleResize)
            }

            return () => {
                window.removeEventListener('resize', handleResize)
                if (window.visualViewport) {
                    window.visualViewport.removeEventListener('resize', handleResize)
                }
            }
        }, [isExpanded])

        useImperativeHandle(ref, () => ({
            expandToNormal: () => {
                setDrawerHeight(minHeight)
                setIsExpanded(false)
                setIsMini(false)
            },
            expandToFull: () => {
                setDrawerHeight(maxHeight)
                setIsExpanded(true)
                setIsMini(false)
            },
            collapseToMini: () => {
                setDrawerHeight(miniHeight)
                setIsExpanded(false)
                setIsMini(true)
            }
        }), [miniHeight, minHeight, maxHeight])

        return (
            <div
                ref={drawerRef}
                className={`slide-up-drawer fixed bottom-0 left-0 right-0 rounded-t-3xl ${resolvedColorScheme === 'dark'
                    ? 'bg-gray-900/75 border-t border-gray-700/50'
                    : 'bg-white/75 border-t border-gray-200/50'
                    } ${isDragging ? 'dragging' : ''}`}
                style={{
                    height: `${drawerHeight}px`,
                    // z-index is handled by CSS class (z-index: 100)
                    // Layering: Map (1) < Drawer (100) < Modals (1000+)
                }}
            >
                {/* Drag Handle Area */}
                <div className="slide-up-drawer-handle flex flex-col items-center pt-3 pb-2">
                    <div
                        className={`drawer-handle-indicator w-12 h-1.5 rounded-full mb-3 ${isDragging
                            ? (resolvedColorScheme === 'dark' ? 'bg-blue-400' : 'bg-blue-500')
                            : (resolvedColorScheme === 'dark' ? 'bg-gray-600' : 'bg-gray-300')
                            } ${isDragging ? 'dragging' : ''}`}
                    />

                    {/* Instructions */}
                    <div className={`text-xs text-center transition-opacity ${resolvedColorScheme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        } ${isDragging ? 'opacity-100' : 'opacity-70'}`}>
                        {isExpanded ? 'Swipe down to collapse' : 'Swipe up to expand'}
                    </div>
                </div>

                {/* Content - Only show when not in mini state */}
                {!isMini && (
                    <div
                        ref={contentRef}
                        className="slide-up-drawer-content flex-1 overflow-y-auto px-4 pb-6"
                        style={{
                            height: `${drawerHeight - 60}px`, // Account for handle area
                            // Improve scroll behavior on mobile, especially iOS Safari
                            WebkitOverflowScrolling: 'touch',
                            overscrollBehavior: 'contain',
                            // Prevent iOS Safari bounce scrolling
                            touchAction: 'pan-y',
                        }}
                    >
                        <div style={{
                            // Add some top padding and ensure proper spacing
                            paddingTop: '8px',
                            minHeight: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            // Ensure content doesn't cause horizontal overflow
                            overflowX: 'hidden',
                        }}>
                            {children}
                        </div>
                    </div>
                )}
            </div>
        )
    })

